import { createSlice } from '@reduxjs/toolkit';

import { storeTokens, clearStoredSession, sessionIsRecoverable } from '../../lib/session';

const resetToSignedOut = (state) => {
  state.user = null;
  state.userHydrated = false;
  state.isAuthenticated = false;
  state.loading = false;
  state.error = null;
  state.otpId = null;
  state.expiresAt = null;
  state.accessToken = null;
  state.refreshToken = null;
  state.tokenExpiresAt = null;
};

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    // Whether the /auth/me/ lookup has finished, successfully or not. Distinguishes
    // "still loading" from "tried and got nothing", which a null user alone cannot.
    userHydrated: false,
    isAuthenticated: false,
    loading: false,
    error: null,
    // Set when a session ended on its own rather than by the user logging out, so the
    // login screen can say why they landed back there.
    sessionExpired: false,
    otpId: null,
    expiresAt: null,
    accessToken: null,
    refreshToken: null,
    tokenExpiresAt: null,
  },
  reducers: {
    // OTP Send
    sendOTPStart: (state) => {
      state.loading = true;
      state.error = null;
      state.sessionExpired = false;
    },
    sendOTPSuccess: (state, action) => {
      state.loading = false;
      state.otpId = action.payload.otp_id;
      state.expiresAt = action.payload.expires_at;
      state.error = null;
    },
    sendOTPFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.otpId = null;
      state.expiresAt = null;
    },
    // OTP Verify
    verifyOTPStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    verifyOTPSuccess: (state, action) => {
      const { access_token, refresh_token, expires_at, user_data } = action.payload;
      if (!access_token || !refresh_token) {
        // Don't half-persist a partial login (would leave 'undefined' strings in localStorage).
        return;
      }
      state.loading = false;
      state.isAuthenticated = true;
      state.userHydrated = false;
      if (user_data !== undefined) {
        state.user = user_data;
      }
      state.accessToken = access_token;
      state.refreshToken = refresh_token;
      state.tokenExpiresAt = expires_at;
      state.error = null;

      storeTokens(access_token, refresh_token);
      if (user_data) {
        localStorage.setItem('user_data', JSON.stringify(user_data));
      }
    },
    verifyOTPFailure: (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    // Google Login
    googleLoginStart: (state) => {
      state.loading = true;
      state.error = null;
      state.sessionExpired = false;
    },
    googleLoginSuccess: (state, action) => {
      const { access_token, refresh_token, expires_at, user_data } = action.payload;
      if (!access_token || !refresh_token) {
        return;
      }
      state.loading = false;
      state.isAuthenticated = true;
      state.userHydrated = false;
      if (user_data !== undefined) {
        state.user = user_data;
      }
      state.accessToken = access_token;
      state.refreshToken = refresh_token;
      state.tokenExpiresAt = expires_at;
      state.error = null;

      storeTokens(access_token, refresh_token);
      if (user_data) {
        localStorage.setItem('user_data', JSON.stringify(user_data));
      }
    },
    googleLoginFailure: (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    // Legacy actions for backward compatibility
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
      state.error = null;
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = action.payload;
    },
    logout: (state) => {
      resetToSignedOut(state);
      state.sessionExpired = false;
      clearStoredSession();
    },
    // Dispatched by the axios interceptor and the tab-focus check when the refresh token
    // is gone or rejected. Same teardown as logout, but flagged so login explains itself.
    sessionExpired: (state) => {
      resetToSignedOut(state);
      state.sessionExpired = true;
      clearStoredSession();
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.userHydrated = true;
      state.isAuthenticated = true;
    },
    userHydrationSettled: (state) => {
      state.userHydrated = true;
    },
    // Initialize auth from localStorage
    initializeAuth: (state) => {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const userData = localStorage.getItem('user_data');

      // Presence alone used to be enough, which let a long-dead session render the whole
      // shell before every call failed. An unusable refresh token means no session.
      if (accessToken && refreshToken && sessionIsRecoverable()) {
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        // Persisted state could flash an access failure before this boot's /auth/me/ lands.
        state.userHydrated = false;

        if (userData) {
          try {
            state.user = JSON.parse(userData);
          } catch (e) {
            console.error('Error parsing user data from localStorage', e);
          }
        }
      } else if (accessToken || refreshToken) {
        clearStoredSession();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('mfa/setMFARequired', (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase('mfa/setEnrollmentRequired', (state) => {
        state.loading = false;
        state.error = null;
      });
  },
});

export const {
  sendOTPStart,
  sendOTPSuccess,
  sendOTPFailure,
  verifyOTPStart,
  verifyOTPSuccess,
  verifyOTPFailure,
  googleLoginStart,
  googleLoginSuccess,
  googleLoginFailure,
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setUser,
  sessionExpired,
  initializeAuth,
  userHydrationSettled,
} = authSlice.actions;
export { authSlice };
